// import types
import type { GridColDef } from '@mui/x-data-grid-pro';
import type { TFunction } from 'i18next';
import type { ServicedeskProcedureRunResponse } from 'generated';

// import hooks
import { useTranslation } from 'react-i18next';

// import utils
import { formatByLocale, formatDateTime } from 'utils/date';

const getPeriod = (row: ServicedeskProcedureRunResponse, t: TFunction) => {
  if (row.procedureCode === 'IMPORT_HOURS_BALANCE') {
    return row.params?.month
      ? formatByLocale(new Date(row.params.month), 'MMMM yyyy')
      : '';
  }
  return `${row.params?.startDate ?? ''} ${t('until')} ${
    row.params?.endDate ?? ''
  }`;
};

const getResult = (row: ServicedeskProcedureRunResponse, t: TFunction) => {
  if (row.result?.error) {
    return t('adminPortal.servicedesk.executedActions.failed', {
      error: row.result.error
    });
  }

  if (row.procedureCode === 'IMPORT_HOURS_BALANCE') {
    // Only the last block writes DONE: anything else means the run stopped early
    // (findings -> PARTIAL, or a failed/abandoned block that left RUNNING behind).
    if (row.result?.status && row.result.status !== 'DONE') {
      return t('adminPortal.servicedesk.hoursBalanceImport.summary.partial', {
        processed:
          (row.result?.updatedCount ?? 0) + (row.result?.skippedCount ?? 0)
      });
    }
    return [
      t('adminPortal.servicedesk.hoursBalanceImport.summary.updated', {
        count: row.result?.updatedCount ?? 0
      }),
      t('adminPortal.servicedesk.hoursBalanceImport.summary.skipped', {
        count: row.result?.skippedCount ?? 0
      }),
      ...(row.result?.clearedCount != null
        ? [
            t('adminPortal.servicedesk.hoursBalanceImport.summary.cleared', {
              count: row.result.clearedCount
            })
          ]
        : [])
    ].join(' · ');
  }

  return t('adminPortal.servicedesk.executedActions.succeeded', {
    dutyLines: row.result?.affectedDutyLines ?? 0,
    revertedExpenseClaims: row.result?.revertedExpenseClaims ?? 0,
    deletedExpenseClaims: row.result?.deletedExpenseClaims ?? 0
  });
};

export const useGetColumnDefs =
  (): GridColDef<ServicedeskProcedureRunResponse>[] => {
    const { t } = useTranslation();

    return [
      {
        field: 'procedureCode',
        headerName: t('adminPortal.servicedesk.executedActions.procedure'),
        flex: 1,
        valueGetter: (_, row) =>
          t(`adminPortal.servicedesk.procedures.${row.procedureCode}.name`)
      },
      {
        field: 'period',
        headerName: t('adminPortal.servicedesk.executedActions.period'),
        flex: 1,
        valueGetter: (_, row) => getPeriod(row, t)
      },
      {
        field: 'result',
        headerName: t('adminPortal.servicedesk.executedActions.result'),
        flex: 1,
        valueGetter: (_, row) => getResult(row, t)
      },
      {
        field: 'executedBy',
        headerName: t('adminPortal.servicedesk.executedActions.executedBy'),
        flex: 1
      },
      {
        field: 'executedAt',
        headerName: t('adminPortal.servicedesk.executedActions.executedAt'),
        flex: 1,
        valueGetter: (_, row) => formatDateTime(row.executedAt)
      }
    ];
  };
